import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, BellRing, CheckCircle2, Eraser, FileSpreadsheet, History,
  Landmark, MapPinned, RefreshCcw, ShieldAlert, TimerOff, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg } from "../../lib/api";

const CATEGORY_ICONS = {
  upload_history: FileSpreadsheet,
  bounced_cheques: Landmark,
  recycle_bin: Trash2,
  notifications: BellRing,
  gps_logs: MapPinned,
  idle_alerts: TimerOff,
};

const fmtDateTime = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

function CategoryRow({ cat, checked, onToggle, disabled }) {
  const Icon = CATEGORY_ICONS[cat.key] || Eraser;
  const empty = !cat.count;
  return (
    <label
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-accent/40 ${checked ? "bg-destructive/[0.04]" : ""} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      data-testid={`reset-category-row-${cat.key}`}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} disabled={disabled}
        className="mt-0.5" data-testid={`reset-category-checkbox-${cat.key}`} />
      <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${checked ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{cat.label}</span>
          {cat.default && <Badge variant="outline" className="text-[10px]">Recommended</Badge>}
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">{cat.description}</span>
      </span>
      <span className={`text-sm font-semibold tabular-nums shrink-0 ${empty ? "text-muted-foreground" : checked ? "text-destructive" : ""}`}
        data-testid={`reset-category-count-${cat.key}`}>
        {fmtNum(cat.count)}
        <span className="block text-[10px] font-normal text-muted-foreground text-right">record{cat.count === 1 ? "" : "s"}</span>
      </span>
    </label>
  );
}

export default function GoLiveReset() {
  const [munis, setMunis] = useState(null);
  const [selCode, setSelCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selected, setSelected] = useState({});
  const [typed, setTyped] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState(null);

  const loadHistory = useCallback(() => {
    api.get("/platform/reset/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => setMunis((r.data || []).filter((m) => m.is_active !== false)))
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
    loadHistory();
  }, [loadHistory]);

  const loadPreview = useCallback((code, { keepResult = false } = {}) => {
    if (!code) { setPreview(null); return; }
    setLoadingPreview(true);
    api.get(`/platform/municipalities/${code}/reset/preview`)
      .then((r) => {
        setPreview(r.data);
        const defaults = {};
        (r.data.categories || []).forEach((c) => { defaults[c.key] = !!c.default; });
        setSelected(defaults);
        setTyped("");
        if (!keepResult) setLastResult(null);
      })
      .catch((e) => { toast.error(errMsg(e)); setPreview(null); })
      .finally(() => setLoadingPreview(false));
  }, []);

  useEffect(() => { loadPreview(selCode); }, [selCode, loadPreview]);

  const cats = preview?.categories || [];
  const core = cats.filter((c) => c.group === "core");
  const extras = cats.filter((c) => c.group !== "core");
  const selectedKeys = cats.filter((c) => selected[c.key]).map((c) => c.key);
  const selectedTotal = useMemo(
    () => cats.filter((c) => selected[c.key]).reduce((s, c) => s + (c.count || 0), 0),
    [cats, selected],
  );
  const codeMatches = !!preview && typed.trim().toLowerCase() === preview.municipality.code;
  const canRun = codeMatches && selectedKeys.length > 0 && !busy;

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const setGroup = (list, value) => setSelected((s) => {
    const n = { ...s };
    list.forEach((c) => { n[c.key] = value; });
    return n;
  });

  const runReset = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/municipalities/${preview.municipality.code}/reset`, {
        categories: selectedKeys, confirm_code: typed.trim(),
      });
      setLastResult(r.data);
      setConfirmOpen(false);
      toast.success(`Go-Live Reset complete — ${fmtNum(r.data.total_deleted)} record${r.data.total_deleted === 1 ? "" : "s"} cleared for ${r.data.municipality.name}. ${r.data.notified} staff notified.`);
      loadPreview(preview.municipality.code, { keepResult: true });
      loadHistory();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Go-Live Reset" subtitle="Wipe test records from a municipality in one go before it goes live — choose exactly what to clear">
        <Button variant="outline" size="sm" onClick={() => { loadPreview(selCode); loadHistory(); }} disabled={!selCode || loadingPreview}
          data-testid="reset-refresh-button">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh counts
        </Button>
      </PageHeader>

      {/* ---- municipality picker ---- */}
      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label>Municipality</Label>
            {!munis ? <Spinner label="Loading municipalities..." /> : (
              <Select value={selCode} onValueChange={setSelCode}>
                <SelectTrigger data-testid="reset-muni-select">
                  <SelectValue placeholder="Select the municipality to reset" />
                </SelectTrigger>
                <SelectContent>
                  {munis.map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`reset-muni-option-${m.code}`}>
                      {m.name} <span className="text-muted-foreground font-mono text-xs">({m.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="text-xs bg-destructive/10 text-destructive rounded-md px-3 py-2 flex gap-2 flex-1" data-testid="reset-warning">
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Everything you tick is <span className="font-semibold">permanently deleted</span> — there is no undo.
              Properties, demands, payments and receipts are never touched by this screen.
            </span>
          </div>
        </CardContent>
      </Card>

      {!selCode ? (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <EmptyState title="Pick a municipality to begin" subtitle="You'll see a live count of every category of test data before anything is deleted." />
          </CardContent>
        </Card>
      ) : loadingPreview || !preview ? <Spinner label="Counting records..." /> : (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* ---- categories ---- */}
          <div className="lg:col-span-2 space-y-5">
            <Card className="shadow-sm">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-sm font-heading">Core go-live cleanup</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">Recommended before every go-live — ticked by default</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setGroup(core, true)} data-testid="reset-core-select-all">All</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setGroup(core, false)} data-testid="reset-core-select-none">None</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 border-t divide-y" data-testid="reset-core-categories">
                {core.map((c) => <CategoryRow key={c.key} cat={c} checked={!!selected[c.key]} onToggle={() => toggle(c.key)} disabled={busy} />)}
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-sm font-heading">Optional extras</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">Off by default — tick only for pure test tenants</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setGroup(extras, true)} data-testid="reset-extras-select-all">All</Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setGroup(extras, false)} data-testid="reset-extras-select-none">None</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 border-t divide-y" data-testid="reset-extra-categories">
                {extras.map((c) => <CategoryRow key={c.key} cat={c} checked={!!selected[c.key]} onToggle={() => toggle(c.key)} disabled={busy} />)}
              </CardContent>
            </Card>
          </div>

          {/* ---- run panel ---- */}
          <div className="space-y-5">
            <Card className="shadow-sm border-destructive/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-destructive">
                  <Eraser className="h-4 w-4" /> Run reset — {preview.municipality.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-muted/60 px-3 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Will permanently delete</p>
                    <p className="text-2xl font-semibold tabular-nums font-heading" data-testid="reset-selected-total">{fmtNum(selectedTotal)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">across</p>
                    <p className="text-sm font-medium" data-testid="reset-selected-categories">{selectedKeys.length} categor{selectedKeys.length === 1 ? "y" : "ies"}</p>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground flex items-start gap-2">
                  <BellRing className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span data-testid="reset-notify-note">
                    {preview.notify_recipients} active Municipal Admin{preview.notify_recipients === 1 ? "" : "s"} / Supervisor{preview.notify_recipients === 1 ? "" : "s"} will receive an in-app alert after the reset.
                  </span>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="reset-confirm-input">
                    Type <span className="font-mono font-semibold text-foreground">{preview.municipality.code}</span> to confirm
                  </Label>
                  <Input id="reset-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)}
                    placeholder={preview.municipality.code} autoComplete="off" disabled={busy}
                    className={typed && !codeMatches ? "border-destructive/60 focus-visible:ring-destructive/40" : ""}
                    data-testid="reset-confirm-input" />
                  {typed && !codeMatches && (
                    <p className="text-[11px] text-destructive" data-testid="reset-confirm-mismatch">Code does not match</p>
                  )}
                </div>

                <Button variant="destructive" className="w-full" disabled={!canRun}
                  onClick={() => setConfirmOpen(true)} data-testid="reset-run-button">
                  <Eraser className="h-4 w-4 mr-1.5" />
                  {selectedKeys.length === 0 ? "Select at least one category" : `Reset ${fmtNum(selectedTotal)} record${selectedTotal === 1 ? "" : "s"}`}
                </Button>
                {selectedKeys.length > 0 && selectedTotal === 0 && (
                  <p className="text-[11px] text-muted-foreground text-center" data-testid="reset-nothing-note">Selected categories are already empty — running is harmless.</p>
                )}
              </CardContent>
            </Card>

            {lastResult && (
              <Card className="shadow-sm border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/20" data-testid="reset-result-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading flex items-center gap-2 text-emerald-800 dark:text-emerald-300">
                    <CheckCircle2 className="h-4 w-4" /> Reset complete
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(lastResult.at)} · {lastResult.notified} staff notified</p>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-sm divide-y">
                    {lastResult.summary.map((s) => (
                      <li key={s.key} className="flex items-center justify-between py-1.5" data-testid={`reset-result-${s.key}`}>
                        <span className="text-muted-foreground">{s.label}</span>
                        <span className="font-medium tabular-nums">{fmtNum(s.deleted)}</span>
                      </li>
                    ))}
                    <li className="flex items-center justify-between py-1.5 font-semibold">
                      <span>Total cleared</span>
                      <span className="tabular-nums" data-testid="reset-result-total">{fmtNum(lastResult.total_deleted)}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ---- history ---- */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4" /> Recent resets</CardTitle>
        </CardHeader>
        <CardContent className="p-0 border-t">
          {!history ? <Spinner /> : history.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4" data-testid="reset-history-empty">No Go-Live Resets have been run yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="reset-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Municipality</TableHead>
                    <TableHead>Cleared</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                    <TableHead>By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h, i) => (
                    <TableRow key={h.id} data-testid={`reset-history-row-${i}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(h.at)}</TableCell>
                      <TableCell>
                        <p className="text-sm">{h.municipality_name}</p>
                        <p className="text-[11px] font-mono text-muted-foreground">{h.municipality_code}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(h.summary || []).map((s) => (
                            <Badge key={s.key} variant="outline" className="text-[10px] font-normal">
                              {s.label}: <span className="font-semibold ml-1 tabular-nums">{fmtNum(s.deleted)}</span>
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium tabular-nums">{fmtNum(h.total_deleted)}</TableCell>
                      <TableCell className="text-sm">{h.by_name || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- final confirmation ---- */}
      <AlertDialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> Permanently delete {fmtNum(selectedTotal)} record{selectedTotal === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  This runs the Go-Live Reset for <span className="font-medium text-foreground">{preview?.municipality.name}</span> and clears:
                </p>
                <ul className="space-y-1">
                  {cats.filter((c) => selected[c.key]).map((c) => (
                    <li key={c.key} className="flex items-center justify-between rounded-md bg-muted/60 px-3 py-1.5">
                      <span>{c.label}</span>
                      <span className="font-medium tabular-nums text-foreground">{fmtNum(c.count)}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-medium text-foreground">This cannot be undone. The action is logged and the municipality's admins and supervisors will be alerted.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runReset} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="reset-confirm-run-button">
              {busy ? "Resetting..." : "Yes, reset now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
